import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useCrumbActionsSetter } from '../../utils/crumbActions';
import SearchSelect from '../SearchSelect';
import AttStatCard from './AttStatCard';
import {
    Box, Button, Card, CardContent, Grid, Typography, TextField, Avatar, Popover, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DateRangeIcon from '@mui/icons-material/DateRange';
import * as XLSX from 'xlsx';
import http from '../../Api/http';
import { GetEmployeeAttendanceOverview } from '../../Api/Api';
import useFinancialYear from '../../hooks/useFinancialYear';
import { toApiDate } from '../../utils/apiFields';
import { SkeletonRows } from '../uiKit';
import SnackBar from '../SnackBar';

const toIsoDate = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const datesForViewPreset = (preset) => {
    const today = new Date();
    const from = new Date();
    const span = preset === '15days' ? 14 : 6;
    from.setDate(today.getDate() - span);
    return { from: toIsoDate(from), to: toIsoDate(today) };
};

const getDayNum = (dateStr) => dateStr.split('-')[0];

const getDayName = (dateStr) => {
    const [d, m, y] = dateStr.split('-');
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${y}-${m}-${d}`).getDay()];
};

const mapStatus = (status = '') => {
    const s = status.toLowerCase().trim();
    if (s === 'present')                     return 'present';
    if (s === 'late')                        return 'late';
    if (s === 'absent')                      return 'absent';
    if (s === 'onleave' || s === 'on leave') return 'leave';
    return 'none';
};

const dayCellText = (dayObj) => {
    const status = mapStatus(dayObj?.status);
    if (status === 'absent') return 'Absent';
    if (status === 'leave')  return 'Leave';
    if (status === 'late')   return dayObj?.loginTime ? `Late (${dayObj.loginTime})` : 'Late';
    if (status === 'present') return dayObj?.loginTime || 'Present';
    return '';
};

const getCellStyle = (status) => {
    const styles = {
        present: { bgcolor: '#E8F5E9', color: '#2E7D32', border: '1px solid #A5D6A7' },
        late:    { bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #FFCC80' },
        absent:  { bgcolor: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A' },
        leave:   { bgcolor: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9' },
        none:    { bgcolor: '#F4F3FB', color: '#bbb',    border: '1px solid #E8E8E8' },
    };
    return styles[status] || styles.none;
};

const LEGEND = [
    { label: 'Present', bgcolor: '#E8F5E9', border: '#A5D6A7' },
    { label: 'Late',    bgcolor: '#FFF3E0', border: '#FFCC80' },
    { label: 'Absent',  bgcolor: '#FFEBEE', border: '#EF9A9A' },
    { label: 'Leave',   bgcolor: '#E3F2FD', border: '#90CAF9' },
];

const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const today = new Date().toISOString().split('T')[0];

const StaffAttendanceOverviewPage = ({ isEmbedded = false }) => {
    const [searchQuery, setSearchQuery]             = useState('');
    const [designationFilter, setDesignationFilter] = useState('All designations');

    const [viewBy, setViewBy]     = useState('7days');
    const [rangeAnchor, setRangeAnchor] = useState(null);
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate]     = useState(today);

    const financialYear = useFinancialYear();

    const [overviewData, setOverviewData] = useState({
        cards: { totalPresent: 0, totalLate: 0, totalLeave: 0, totalAbsent: 0 },
        dateHeaders: [],
        details: [],
        staffCount: 0,
        fromDate: '',
        toDate: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    const [snackOpen, setSnackOpen]       = useState(false);
    const [snackStatus, setSnackStatus]   = useState(false);
    const [snackColor, setSnackColor]     = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const showSnack = (msg, success) => {
        setSnackMessage(msg); setSnackOpen(true); setSnackColor(success); setSnackStatus(success);
    };

    const fetchOverview = useCallback(async () => {
        if (viewBy === 'custom' && (!fromDate || !toDate)) return;
        if (!financialYear) return;

        const range = viewBy === 'custom'
            ? { from: fromDate, to: toDate }
            : datesForViewPreset(viewBy);

        setIsLoading(true);
        try {
            const res = await http.get(GetEmployeeAttendanceOverview, {
                params: {
                    financialYear,
                    fromDate: toApiDate(range.from),
                    toDate:   toApiDate(range.to),
                },
            });
            if (res.data && !res.data.error) {
                setOverviewData({
                    cards:       res.data.cards         || {},
                    dateHeaders: res.data.dateHeaders   || [],
                    details:     res.data.details       || [],
                    staffCount:  res.data.employeeCount || 0,
                    fromDate:    res.data.fromDate      || '',
                    toDate:      res.data.toDate        || '',
                });
            } else {
                showSnack(res.data?.message || 'Failed to fetch data', false);
            }
        } catch (error) {
            console.error('Employee attendance overview error:', error);
            showSnack(error?.response?.data?.message || 'Failed to load the attendance overview', false);
        } finally {
            setIsLoading(false);
        }
    }, [viewBy, fromDate, toDate, financialYear]);

    useEffect(() => {
        if (viewBy !== 'custom') {
            fetchOverview();
        }
    }, [viewBy, financialYear]);

    const designations = useMemo(
        () => ['All designations', ...Array.from(new Set(overviewData.details.map(s => s.designation).filter(Boolean))).sort()],
        [overviewData.details]
    );

    const filteredDetails = overviewData.details.filter(s => {
        const q = searchQuery.trim().toLowerCase();
        const matchesSearch = !q
            || (s.name || '').toLowerCase().includes(q)
            || String(s.employeeCode || '').toLowerCase().includes(q);
        const matchesDesignation = designationFilter === 'All designations' || s.designation === designationFilter;
        return matchesSearch && matchesDesignation;
    });

    const { cards, dateHeaders } = overviewData;

    const STAT_CARDS = [
        { title: 'Total Present', value: cards.totalPresent ?? 0, icon: CheckCircleIcon, tone: 'green' },
        { title: 'Total Late',    value: cards.totalLate    ?? 0, icon: AccessTimeIcon,  tone: 'amber' },
        { title: 'Total Leave',   value: cards.totalLeave   ?? 0, icon: EventBusyIcon,   tone: 'purple' },
        { title: 'Total Absent',  value: cards.totalAbsent  ?? 0, icon: CancelIcon,      tone: 'rose' },
    ];

    const viewLabel = viewBy === '7days' ? 'Last 7 days' : viewBy === '15days' ? 'Last 15 days' : 'Custom range';

    const handleExportExcel = () => {
        if (!filteredDetails.length) {
            showSnack('No attendance data to export.', false);
            return;
        }
        const header = ['Employee Code', 'Staff Member', 'Designation', 'Department',
            ...dateHeaders.map((d) => `${d} (${getDayName(d)})`)];
        const rows = filteredDetails.map((staff) => ([
            staff.employeeCode || '',
            staff.name || '',
            staff.designation || '',
            staff.department || '',
            ...dateHeaders.map((_, i) => dayCellText(staff.days?.[i])),
        ]));
        const aoa = [
            ['Staff Attendance Overview'],
            [`View: ${viewLabel}`, '', `Range: ${dateHeaders[0] || ''} to ${dateHeaders[dateHeaders.length - 1] || ''}`],
            [`Staff: ${filteredDetails.length}`, '', `Days: ${dateHeaders.length}`],
            [],
            header,
            ...rows,
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 14 }, { wch: 24 }, { wch: 18 }, { wch: 18 }, ...dateHeaders.map(() => ({ wch: 14 }))];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
        const stamp = (dateHeaders[0] || today).replace(/[^0-9]/g, '');
        XLSX.writeFile(wb, `Attendance_Overview_${stamp || 'export'}.xlsx`);
        showSnack('Attendance exported to Excel.', true);
    };

    // The three filter controls. On this page they sit in the breadcrumb row; when the page is
    // embedded inside another one (no breadcrumb of its own) they stay in an in-page card.
    const searchField = (
        <TextField
            size="small"
            placeholder="Search staff name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 17, color: '#aaa' }} />
                        </InputAdornment>
                    ),
                },
            }}
            sx={{ width: { xs: 180, md: 300 }, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '7px', height: 38, fontSize: '13px' } }}
        />
    );
    const designationSelect = (
        <Box sx={{ width: 180, flexShrink: 0 }}>
            <SearchSelect
                value={designationFilter} onChange={(e) => setDesignationFilter(e.target.value)} searchPlaceholder="Search designations…"
                sx={{ bgcolor: '#fff', borderRadius: '7px', '& .MuiOutlinedInput-root': { height: 38, fontSize: '13px', borderRadius: '7px' } }}
                options={designations.map((t) => ({ value: t, label: t }))}
            />
        </Box>
    );
    // The calendar icon is the "From - To" control: clicking it opens the date-range panel.
    const rangeButton = (
        <Box
            role="button"
            aria-label="View by date range"
            onClick={(e) => setRangeAnchor(e.currentTarget)}
            sx={{
                width: 38, height: 38, borderRadius: '9px', cursor: 'pointer', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: viewBy === 'custom' ? '#E9E3FD' : '#F3F0FE',
                color: '#8F7BF0',
                border: `1px solid ${viewBy === 'custom' ? '#B9A9F7' : '#DDD5FB'}`,
                transition: 'all 0.18s',
                '&:hover': { bgcolor: '#E9E3FD' },
            }}
        >
            <DateRangeIcon sx={{ fontSize: 20 }} />
        </Box>
    );

    const setCrumbActions = useCrumbActionsSetter();
    // The export handler is read through a ref so the breadcrumb buttons don't have to be re-published
    // (which re-renders the whole layout) every time the table data changes.
    const exportRef = useRef(null);
    exportRef.current = handleExportExcel;
    useEffect(() => {
        if (isEmbedded || !setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {searchField}
                {designationSelect}
                {rangeButton}
                <Button startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />} onClick={() => exportRef.current && exportRef.current()}
                    sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, color: '#7C5CFC', bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '7px', height: 38, px: 2, '&:hover': { bgcolor: '#F8FAFC' } }}>
                    Export to Excel
                </Button>
            </Box>,
        );
        return () => setCrumbActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEmbedded, setCrumbActions, viewBy, searchQuery, designationFilter, designations]);

    return (
        <>
        <SnackBar open={snackOpen} color={snackColor} setOpen={setSnackOpen} status={snackStatus} message={snackMessage} />
        <Box sx={{ p: isEmbedded ? 0 : 2 }}>
            {isEmbedded && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 2, py: 1.1, bgcolor: '#fff',
                    border: '1px solid #E6EAF1', borderRadius: '10px', flexWrap: 'wrap',
                }}>
                    {searchField}
                    {designationSelect}
                    {rangeButton}
                </Box>
            )}

                <Popover
                    open={Boolean(rangeAnchor)}
                    anchorEl={rangeAnchor}
                    onClose={() => setRangeAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { p: 2, mt: 1, width: 300, borderRadius: '10px' } } }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A', mb: 1.4 }}>Select date range</Typography>
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: viewBy === 'custom' ? 1.6 : 0 }}>
                        {[['7days', '7 days'], ['15days', '15 days'], ['custom', 'Custom']].map(([key, label]) => (
                            <Chip
                                key={key} label={label} clickable
                                onClick={() => { setViewBy(key); if (key !== 'custom') setRangeAnchor(null); }}
                                sx={{
                                    height: 32, fontSize: 12.5, fontWeight: 700, borderRadius: '9px',
                                    bgcolor: viewBy === key ? '#7C5CFC' : '#F3F0FE',
                                    color: viewBy === key ? '#fff' : '#7C5CFC',
                                    border: `1px solid ${viewBy === key ? '#7C5CFC' : '#DDD5FB'}`,
                                    '&:hover': { bgcolor: viewBy === key ? '#6246E0' : '#E9E3FD' },
                                }}
                            />
                        ))}
                    </Box>
                    {viewBy === 'custom' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                        <TextField
                            label="From" type="date" size="small" fullWidth value={fromDate}
                            slotProps={{ inputLabel: { shrink: true } }}
                            onChange={(e) => {
                                const v = e.target.value;
                                setFromDate(v);
                                // "To" follows automatically: it fills in, and moves forward if it would fall before "From".
                                if (!toDate || toDate < v) setToDate(v);
                            }}
                        />
                        <TextField
                            label="To" type="date" size="small" fullWidth value={toDate}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: fromDate || undefined } }}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                        <Button
                            variant="contained"
                            onClick={() => { fetchOverview(); setRangeAnchor(null); }}
                            disabled={!fromDate || !toDate}
                            sx={{
                                textTransform: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: '600',
                                '&&': {
                                    bgcolor: '#7C5CFC', color: '#fff', border: 'none',
                                    '&:hover': { bgcolor: '#6246E0' },
                                    '&.Mui-disabled': { bgcolor: '#C9BEFB', color: '#fff', opacity: 1 },
                                },
                            }}
                        >
                            Apply
                        </Button>
                    </Box>
                    )}
                </Popover>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {STAT_CARDS.map((card) => (
                    <Grid key={card.title} size={{ xs: 6, sm: 3, md: 3, lg: 3 }}>
                        <AttStatCard label={card.title} value={card.value} sub={viewLabel} icon={card.icon} tone={card.tone} />
                    </Grid>
                ))}
            </Grid>

            <Card sx={{ border: '1px solid #ECEBF5', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)' }}>
                <CardContent sx={{ p: '0 !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2.2 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                            Detailed Attendance Log
                        </Typography>
                    </Box>

                    {isLoading ? (
                        <Box sx={{ px: 3, pb: 3 }}><SkeletonRows count={5} /></Box>
                    ) : filteredDetails.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                            <Typography sx={{ fontSize: '15px', fontWeight: '600', mb: 0.5 }}>No Staff Members Found</Typography>
                            <Typography sx={{ fontSize: '12px' }}>Try adjusting your search or filter criteria</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Attached to the card: edge to edge under the heading, no inner box. */}
                            <TableContainer sx={{
                                overflowX: 'auto', borderTop: '1px solid #E8E8E8',
                            }}>
                                <Table size="small" sx={{ minWidth: 1120 }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F4F3FB' }}>
                                            <TableCell sx={{
                                                position: 'sticky', left: 0, bgcolor: '#F4F3FB', zIndex: 2,
                                                borderRight: '2px solid #E8E8E8', fontWeight: '600',
                                                fontSize: '12px', color: '#666', textTransform: 'uppercase', minWidth: 260,
                                            }}>
                                                Staff Member
                                            </TableCell>
                                            {dateHeaders.map(dateStr => (
                                                <TableCell key={dateStr} align="center" sx={{
                                                    bgcolor: '#F4F3FB',
                                                    fontWeight: '600', fontSize: '11px', minWidth: 110,
                                                    borderLeft: '1px solid #E8E8E8', py: 1.4,
                                                }}>
                                                    <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#333' }}>
                                                        {getDayNum(dateStr)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '11.5px', color: '#aaa' }}>
                                                        {getDayName(dateStr)}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredDetails.map(staff => (
                                            <TableRow key={staff.employeeCode} sx={{ '&:hover': { bgcolor: '#FAFAFA' } }}>
                                                <TableCell sx={{
                                                    position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1,
                                                    borderRight: '2px solid #E8E8E8',
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                        <Avatar sx={{ width: 42, height: 42, bgcolor: '#7C5CFC', fontSize: '14px', fontWeight: '700' }}>
                                                            {getInitials(staff.name)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography sx={{ fontSize: '14.5px', fontWeight: '600', color: '#1a1a1a' }}>
                                                                {staff.name}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '11.5px', color: '#999' }}>
                                                                {staff.employeeCode}
                                                                {staff.designation ? ` · ${staff.designation}` : ''}
                                                                {staff.department ? ` · ${staff.department}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                {staff.days.map((dayObj) => {
                                                    const status = mapStatus(dayObj.status);
                                                    const cellStyle = getCellStyle(status);
                                                    return (
                                                        <TableCell key={dayObj.date} align="center" sx={{
                                                            ...cellStyle,
                                                            borderLeft: '1px solid #E8E8E8', py: 2, px: 0.8,
                                                            cursor: 'default',
                                                        }}>
                                                            {status === 'none' ? (
                                                                <Typography sx={{ fontSize: '12px', color: '#ccc' }}>—</Typography>
                                                            ) : status === 'absent' ? (
                                                                <Typography sx={{ fontSize: '12px', fontWeight: '700' }}>Absent</Typography>
                                                            ) : status === 'leave' ? (
                                                                <Typography sx={{ fontSize: '12px', fontWeight: '700' }}>Leave</Typography>
                                                            ) : status === 'late' ? (
                                                                <Typography sx={{ fontSize: '12px', fontWeight: '700' }}>
                                                                    {dayObj.loginTime || 'Late'}
                                                                </Typography>
                                                            ) : (
                                                                <Typography sx={{ fontSize: '12px', fontWeight: '600' }}>
                                                                    {dayObj.loginTime || '✓'}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: 'flex', gap: 2, px: 3, py: 2, borderTop: '1px solid #E8E8E8', flexWrap: 'wrap' }}>
                                {LEGEND.map(l => (
                                    <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: l.bgcolor, border: `1px solid ${l.border}` }} />
                                        <Typography sx={{ fontSize: '11px', color: '#666' }}>{l.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
        </>
    );
};

export default StaffAttendanceOverviewPage;
